import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { InlineNotice } from 'components/enterprise-shell';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { DineRequisition } from 'api/types';
import { STATUS_BADGE, STATUS_LABEL, dateOnly, dateTime, kitchenProgress, money, requisitionActions } from './requisitionLabels';

/**
 * Requisition Details (REQ-001, R1): read-only, per the template's view modal, with the
 * requisition's own actions in the footer, because each needs the reviewed record:
 *   Send to kitchen  REQUESTED, or SENT with a dish a closed station held back
 *   Issue            SENT, once every live ticket is ready; asks who received it
 *   Cancel           before the kitchen (Delete's place: bottom left); asks why
 *   Approve / Reject awaiting approval, for a holder of the routed role (S3); reject asks why
 * Send and Issue run in the working outlet, which must be the requisition's. The rules are
 * requisitionActions, shared with the list's row menu, which can open this view straight on
 * Issue, Cancel, Approve or Reject (initialAction).
 */

type Step = 'issue' | 'cancel' | 'approve' | 'reject';

interface Props {
  isOpen: boolean;
  record: DineRequisition | null;
  /** The outlet in the header; Send and Issue need it to be the requisition's. */
  workingOutletId: string;
  canEdit: boolean;
  canIssue: boolean;
  canCancel: boolean;
  busy: boolean;
  /** Opened from the row menu's Issue…, Cancel requisition…, Approve… or Reject…: start on that step when it applies. */
  initialAction?: Step | null;
  tabId?: string;
  moduleName: string;
  onClose: () => void;
  onEdit: (record: DineRequisition) => void;
  onSend: (record: DineRequisition) => void;
  onIssue: (record: DineRequisition, receivedByName: string) => void;
  onCancel: (record: DineRequisition, reason: string) => void;
  onApprove: (record: DineRequisition, note: string | undefined) => void;
  onReject: (record: DineRequisition, reason: string) => void;
  onRestoreView: (recordId: string) => void;
}

const ViewField: React.FC<{ label: string; value?: React.ReactNode; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px',
    }}>
      {value === undefined || value === null || value === '' ? <span style={{ color: 'var(--text-muted)' }}>—</span> : value}
    </div>
  </div>
);

const cell: React.CSSProperties = { padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border-color)' };
const num: React.CSSProperties = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const DineOpsRequisitionViewModal: React.FC<Props> = ({
  isOpen, record, workingOutletId, canEdit, canIssue, canCancel, busy, initialAction, tabId, moduleName,
  onClose, onEdit, onSend, onIssue, onCancel, onApprove, onReject, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [action, setAction] = useState<Step | null>(null);
  const [text, setText] = useState('');

  // A fresh record (after an action, or another requisition) starts on the step the row menu asked
  // for, if it still applies; otherwise with no action open.
  useEffect(() => {
    setText('');
    if (!record || !initialAction) { setAction(null); return; }
    const a = requisitionActions(record, workingOutletId, { canEdit, canIssue, canCancel });
    const fits = initialAction === 'issue' ? a.mayIssue && a.ready && !a.notHere
      : initialAction === 'cancel' ? a.mayCancel : a.mayDecide;
    setAction(fits ? initialAction : null);
  }, [record?.id, record?.rowVersion, initialAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMinimize = useCallback(() => {
    if (!tabId || !record) return;
    const id = record.id;
    minimize({ id: `${tabId}-view-modal-${id}`, title: `View - ${record.requisitionNumber}`, moduleName, tabId, onRestore: () => onRestoreView(id) });
    onClose();
  }, [tabId, record, minimize, moduleName, onClose, onRestoreView]);

  if (!isOpen || !record) return null;
  const r = record;
  const lines = r.lines ?? [];
  const issued = r.requisitionStatus === 'ISSUED';
  const { held, notHere: elsewhere, ready, notReady, mayEdit, maySend, mayIssue, mayCancel, mayDecide, awaiting } =
    requisitionActions(r, workingOutletId, { canEdit, canIssue, canCancel });
  const needsText = action === 'issue' || action === 'cancel' || action === 'reject';
  const here = !elsewhere;

  return (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Requisition {r.requisitionNumber}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" type="button"><IconSystem.chevronDown size={16} /></button>
            )}
            <button className="modal-close" onClick={onClose} type="button" disabled={busy}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {awaiting && (
            <div style={{ marginBottom: '0.75rem' }}>
              <InlineNotice tone="warning" text={awaiting}
                detail={mayDecide ? 'You hold that role: approve it to let it go to the kitchen, or reject it.' : 'It goes to the kitchen once approved.'} />
            </div>
          )}
          {(maySend || mayIssue) && elsewhere && <div style={{ marginBottom: '0.75rem' }}><InlineNotice tone="warning" text={elsewhere} /></div>}
          {r.requisitionStatus === 'SENT' && held > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <InlineNotice tone="warning" text={`${held} dish(es) waiting for a closed station`} detail="Send again when the station opens, or ask the kitchen to open its fallback." />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Prepared by (outlet)" value={`${r.outletName} (${r.outletCode})`} />
            <ViewField label="For (department)" value={r.requestingUnitName} />
            <ViewField label="Purpose" value={r.purposeName ?? r.purpose} />
            <ViewField label="Status" value={<>
              <span className={`badge ${STATUS_BADGE[r.requisitionStatus]}`}>{STATUS_LABEL[r.requisitionStatus]}</span>
              {kitchenProgress(r) && <span style={{ marginLeft: 8, fontSize: '0.8rem' }} data-kitchen-progress>{kitchenProgress(r)}</span>}
            </>} />
            <ViewField label="Needed at" value={dateTime(r.neededAt)} />
            <ViewField label="Raised" value={`${dateOnly(r.businessDate)} · ${r.createdByName ?? ''}`} />
            {r.orderNumber && <ViewField label="Kitchen order" value={<span style={{ fontFamily: 'monospace' }}>{r.orderNumber}</span>} />}
            {r.sentAt && <ViewField label="Sent to the kitchen" value={`${dateTime(r.sentAt)} · ${r.sentByName ?? ''}`} />}
            {issued && <ViewField label="Issued" value={`${dateTime(r.issuedAt)} · ${r.issuedByName ?? ''} · business date ${dateOnly(r.issueBusinessDate)}`} />}
            {issued && <ViewField label="Received by" value={r.receivedByName} />}
            {r.requisitionStatus === 'CANCELLED' && <ViewField label="Cancelled" value={`${dateTime(r.cancelledAt)} · ${r.cancelledByName ?? ''}`} />}
            {r.requisitionStatus === 'CANCELLED' && <ViewField label="Reason" value={r.cancelReason} />}
            {r.approvedAt && <ViewField label="Approved" value={`${dateTime(r.approvedAt)} · ${r.approvedByName ?? ''}`} />}
            {r.requisitionStatus === 'REJECTED' && <ViewField label="Rejected" value={`${dateTime(r.rejectedAt)} · ${r.rejectedByName ?? ''}`} />}
            {r.requisitionStatus === 'REJECTED' && <ViewField label="Why rejected" value={r.rejectedReason} />}
            <ViewField label="Remarks" value={r.remarks} fullWidth />
          </div>

          <div style={{ marginTop: '1.25rem' }}>
            <label className="form-label">Dishes ({lines.length})</label>
            <div style={{ border: '1px solid var(--border-color)', borderRadius: '0.375rem', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    <th style={cell}>Dish</th>
                    <th style={{ ...num, textAlign: 'right' }}>Qty</th>
                    <th style={cell}>Note</th>
                    <th style={cell}>Kitchen</th>
                    <th style={{ ...num, textAlign: 'right' }}>{issued ? 'Issued' : ''}</th>
                    <th style={{ ...num, textAlign: 'right' }}>Unit value</th>
                    <th style={{ ...num, textAlign: 'right' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const unit = issued ? (l.transferUnitPrice ?? 0) : l.estimatedUnitPrice;
                    const amount = issued ? (l.transferAmount ?? 0) : l.estimatedUnitPrice * l.quantity;
                    return (
                      <tr key={l.id}>
                        <td style={cell}>{l.itemName}</td>
                        <td style={num}>{l.quantity}</td>
                        <td style={cell}>{l.note ?? ''}</td>
                        <td style={cell}>{l.lineStatus ? l.lineStatus.charAt(0) + l.lineStatus.slice(1).toLowerCase() : <span style={{ color: 'var(--text-muted)' }}>Not sent</span>}</td>
                        <td style={num}>{issued ? l.issuedQuantity ?? 0 : ''}</td>
                        <td style={num}>{money(unit)}</td>
                        <td style={num}>{money(amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ ...cell, fontWeight: 600 }} colSpan={6}>{issued ? 'Transfer value (fixed at issue, no tax)' : "Transfer value at today's prices (no tax)"}</td>
                    <td style={{ ...num, fontWeight: 600 }}>{money(issued ? r.transferTotal : r.estimatedTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {action && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.375rem', background: 'var(--bg-secondary)' }}
              data-requisition-action={action}>
              <label className="form-label">
                {action === 'issue' ? 'Received by' : action === 'cancel' ? 'Why is it cancelled?' : action === 'reject' ? 'Why is it rejected?' : 'Note (optional)'}
                {needsText && <span className="text-danger"> *</span>}
              </label>
              <input className="form-input" autoFocus value={text} maxLength={action === 'issue' ? 150 : 300} disabled={busy}
                placeholder={action === 'issue' ? 'Who in the department took it' : action === 'approve' ? 'e.g., Agreed with the GM' : action === 'reject' ? 'e.g., Not budgeted this month' : 'e.g., Group arrival postponed'}
                onChange={(e) => setText(e.target.value)} />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                {action === 'issue'
                  ? `Ready tickets are marked served, and ${r.requestingUnitName} is charged ${money(r.estimatedTotal)} at transfer value. This cannot be undone.`
                  : action === 'approve' ? `${money(r.estimatedTotal)} at transfer value. Once approved it can be sent to the kitchen.`
                  : action === 'reject' ? 'Rejected is final. Copy it to raise it again.'
                  : 'The requisition stays on the list as cancelled.'}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {mayCancel && !action && (
            <button className="btn btn-danger" type="button" style={{ marginRight: 'auto' }} disabled={busy} onClick={() => setAction('cancel')}>
              <IconSystem.x size={16} /> Cancel requisition
            </button>
          )}
          {action ? (
            <>
              <button className="btn btn-secondary" type="button" disabled={busy} onClick={() => { setAction(null); setText(''); }}>Back</button>
              <button className={`btn ${action === 'cancel' || action === 'reject' ? 'btn-danger' : 'btn-primary'}`} type="button" disabled={busy || (needsText && !text.trim())}
                onClick={() => {
                  const t = text.trim();
                  if (action === 'issue') onIssue(r, t);
                  else if (action === 'cancel') onCancel(r, t);
                  else if (action === 'approve') onApprove(r, t || undefined);
                  else onReject(r, t);
                }}>
                {action === 'issue' ? 'Issue' : action === 'cancel' ? 'Cancel requisition' : action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose} type="button" disabled={busy}>Close</button>
              {mayEdit && (
                <button className="btn btn-secondary" onClick={() => onEdit(r)} type="button" disabled={busy}><IconSystem.edit size={16} /> Edit</button>
              )}
              {mayDecide && (
                <>
                  <button className="btn btn-danger" type="button" disabled={busy} onClick={() => setAction('reject')}>Reject</button>
                  <button className="btn btn-primary" type="button" disabled={busy} onClick={() => setAction('approve')}>Approve</button>
                </>
              )}
              {maySend && (
                <button className="btn btn-primary" type="button" disabled={busy || !here} title={elsewhere} onClick={() => onSend(r)}>
                  {r.requisitionStatus === 'SENT' ? 'Send again' : 'Send to kitchen'}
                </button>
              )}
              {mayIssue && (
                <button className="btn btn-primary" type="button" disabled={busy || !here || !ready}
                  title={elsewhere ?? notReady}
                  onClick={() => setAction('issue')}>
                  Issue
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DineOpsRequisitionViewModal;
